/// <amd-module name="Build/Designer/TemplateSelector" />
import * as React from "react";

import { TemplateConstants, TemplateParameterTypes, TemplateRequiredConstants } from "Build/Designer/Constants";
import { PageLoadScenarios, PerformanceConstants } from "Build/Designer/Performance";
import * as Resources from "Build/Designer/Resources";
import { ConfigurationFileInfo, RepositoryAnalysisInfo, TemplateInfo, WizardState, FileAsset } from "Build/Designer/Wizard.types";
import { IWizardListItem, WizardList } from "Build/Designer/WizardList";

import { EndpointHelper } from "Build/DesignerExtensions/YamlSnippetForm/Utilities/EndpointHelper";

import { ProgressIndicator } from "OfficeFabric/ProgressIndicator";

import { CustomizedTemplateDataProviderRequest } from "Pipelines/Common/Library/Contracts/DataProvider";

import * as StringUtil from "VSS/Core/Util/String";
import { IHtmlNormalizerService } from "VSS/Features/Markdown/Interfaces/IHtmlNormalizerService";
import { IVssObservableService, ServiceOptions, Services, VssObservableService } from "VSS/Platform/Context";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Button } from "VSSUI/Button";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { UncheckedObserver } from "VSSUI/Observer";
import { RepositoryTypes } from "Build/SourceProviders/Common";

export interface Props {
    wizardState: WizardState;
    onTemplateSelected: (template?: TemplateInfo, repoAnalysis?: RepositoryAnalysisInfo, configuration?: ConfigurationFileInfo, branch?: string, configPath?: string) => void;
}

interface SelectedTemplate {
    template: TemplateInfo,
    repoAnalysis?: RepositoryAnalysisInfo,
    configuration?: ConfigurationFileInfo
}

export interface State {
    progress: number;
    loadComplete: boolean;
    errorMessage: string;
    incompleteTemplate?: SelectedTemplate;
    fileSelectPanelIsOpen: boolean;
    requiredInfoPanelIsOpen: boolean;
    renderingTemplate: boolean;
}

const bringYourOwnYamlId = "bringYourOwn"; // The id for the "bring your own" template

export class TemplateSelector extends VssComponent<Props, State, IVssComponentContext> {
    private static readonly s_progressBarMax = 0.99; // go to 99% max
    private static readonly s_progressBarExistingResults = TemplateSelector.s_progressBarMax * 0.3; // 30%
    private static readonly s_progressBarTemplateResults = TemplateSelector.s_progressBarMax * 0.3; // 30%
    private static readonly s_progressBarStep =
        (TemplateSelector.s_progressBarMax - TemplateSelector.s_progressBarExistingResults) / 55; // 5.5 seconds

    private _existingConfigFileFetched: boolean;
    private _progressTicker: Promise<void>;
    private _markdownPromise: Promise<any>;
    private _mounted: boolean;
    private _templatesFetched: boolean;
    private _creatingResources: boolean;
    private _createdResources: boolean;
    private _endpointStatusCompleted: boolean;
    private _fetchedCustomizedTemplates: boolean;

    private _recommendedConfigPath: string;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            progress: 0,
            loadComplete: false,
            errorMessage: "",
            fileSelectPanelIsOpen: false,
            requiredInfoPanelIsOpen: false,
            renderingTemplate: false
        };

        this._progressTicker = this._getPromiseTicker();

        this._existingConfigFileFetched = false;
        this._creatingResources = false;
        this._createdResources = false;
        this._endpointStatusCompleted = false;
        this._fetchedCustomizedTemplates = false;
        this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService").startScenario(PageLoadScenarios.Templates, false);
    }

    public render(): JSX.Element {
        const templateService = this.context.pageContext.getService<IBuildTemplateService>("IBuildTemplateService");
        const requiredParameters = !!this.state.incompleteTemplate &&
            !!this.state.incompleteTemplate.template.parameters &&
            this.state.incompleteTemplate.template.parameters;

        return <div className="template-selector">
            <div className="header-section">
                <div className="subheader body-m secondary-text">{Resources.NewPipeline}</div>
                <div className="header title-l">{Resources.ConfigureYourPipeline}</div>
            </div>
            {
                !this.state.renderingTemplate &&
                <>
                    {this.state.errorMessage && this.state.loadComplete &&
                        <MessageCard severity={MessageCardSeverity.Error}>
                            {
                                /* Some of the server returned error messages contain mark down links for learn more. */
                                /* Hence the need to render them as html. The strings are being normalized to remove any un-wanted content like 'script' tags. */
                                /* tslint:disable-next-line:react-no-dangerous-html */
                                <span dangerouslySetInnerHTML={{ __html: this.state.errorMessage }} ></span>
                            }
                        </MessageCard>
                    }
                    <UncheckedObserver templateData={templateService}>
                        <TemplatesView
                            sourceProvider={this.props.wizardState.sourceProvider}
                            templateData={undefined}
                            loadComplete={this.state.loadComplete}
                            onTemplateSelected={this._selectTemplate}
                            progress={this.state.progress} />
                    </UncheckedObserver>
                    {
                        this.state.fileSelectPanelIsOpen &&
                        <WrappedComponent
                            wrappedType={"designer-file-select-panel"}
                            dependencies={["ms.vss-build-web.designer-extensions.file-select-panel"]}
                            wrappedProps={{
                                wizardState: this.props.wizardState,
                                isOpen: this.state.fileSelectPanelIsOpen,
                                onDismiss: this._dismissFileSelectPanel,
                                onTemplateSelected: this._onTemplateSelected,
                            }}
                        />
                    }
                    {
                        !!requiredParameters &&
                        requiredParameters.length > 0 &&
                        this.state.incompleteTemplate &&
                        <WrappedComponent
                            wrappedType={"designer-required-template-info-panel"}
                            dependencies={["ms.vss-build-web.designer-extensions.required-template-info-panel"]}
                            wrappedProps={{
                                template: this.state.incompleteTemplate.template,
                                requires: requiredParameters,
                                isOpen: this.state.requiredInfoPanelIsOpen,
                                onRequiredTemplateInfoCompleted: this._completeTemplate,
                                onDismiss: this._dismissRequiredInfoPanel,
                                onError: this._handleError
                            }}
                        />
                    }
                </>
            }
            {
                this.state.renderingTemplate &&
                <React.Fragment>
                    {this.state.errorMessage &&
                        <MessageCard severity={MessageCardSeverity.Error}>
                            {
                                /* Some of the server returned error messages contain mark down links for learn more. */
                                /* Hence the need to render them as html. The strings are being normalized to remove any un-wanted content like 'script' tags. */
                                /* tslint:disable-next-line:react-no-dangerous-html */
                                <span dangerouslySetInnerHTML={{ __html: this.state.errorMessage }}></span>
                            }
                        </MessageCard>
                    }
                    {!this.state.errorMessage &&
                        <React.Fragment>
                            <div>{Resources.ConfiguringYourPipelineAndGeneratingYaml}</div>
                            <ProgressIndicator className="progress" percentComplete={this.state.progress} />
                        </React.Fragment>
                    }
                </React.Fragment>
            }
        </div>;
    }

    public componentDidMount(): void {
        this._mounted = true;
        this._existingConfigFileFetched = false;
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getDataAsync<ConfigurationFileInfo>("ms.vss-build-web.existing-configuration-data-provider", this.props.wizardState)
            .then((configuration: ConfigurationFileInfo) => {
                this._existingConfigFileFetched = true;
                if (configuration) {
                    this.props.onTemplateSelected(undefined, undefined, configuration);
                    this._progressTicker = Promise.resolve();
                }
                else if (this._mounted) {
                    this.setState({
                        progress: this.state.progress + TemplateSelector.s_progressBarExistingResults,
                        loadComplete: this._existingConfigFileFetched
                    });
                }
            });

        this._templatesFetched = false;
        const templateService = this.context.pageContext.getService<IBuildTemplateService>("IBuildTemplateService");
        templateService.subscribe(this._onTemplatesRetrieved);
        templateService.getTemplates(this.props.wizardState);

        this._markdownPromise = contributionService.getContributionAsync("ms.vss-features.markdown");
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        const templateService = this.context.pageContext.getService<IBuildTemplateService>("IBuildTemplateService");
        templateService.unsubscribe(this._onTemplatesRetrieved);
        this._mounted = false;
    }

    private _selectTemplate = (template?: TemplateInfo, repoAnalysis?: RepositoryAnalysisInfo, configuration?: ConfigurationFileInfo) => {
        const parameterInfo = (template && template.parameters && template.parameters.filter(s => !!s)) || [];
        if (parameterInfo.length > 0) {
            template!.parameters = parameterInfo;
            this.setState({
                progress: 0,
                incompleteTemplate: {
                    template: template,
                    repoAnalysis: repoAnalysis,
                    configuration: configuration
                },
                requiredInfoPanelIsOpen: true
            } as State);
        }
        else if (template?.id === bringYourOwnYamlId) {
            this._onTemplateSelected(template, repoAnalysis, undefined);
        }
        else {
            // When template is selected without using parameter panel, applies create-resources-data-provider workflow so backend replaces mustache templating
            this.setState({
                progress: 0,
                incompleteTemplate: {
                    template: template,
                    repoAnalysis: repoAnalysis,
                    configuration: configuration
                }
            } as State, () => this._completeTemplate({}));
        }
    }

    private _completeTemplate = async (params: { [key: string]: object }): Promise<void> => {
        if (!!this.state.incompleteTemplate) {
            const incompleteTemplate = this.state.incompleteTemplate;
            const templateParams = this._getTemplateParameters(incompleteTemplate.template);
            this.setState({
                renderingTemplate: true
            });

            this._creatingResources = true;
            this.monitorAndUpdateProgress();
            this._recordTelemetryInfo("CreatingResources");
            const result: { [key: string]: object } = this._parseResponse(await this._createRequiredResources(params));

            this._createdResources = true;
            this._recordTelemetryInfo("WaitingForEndpointsToBeReady");

            const endpointParameters = (incompleteTemplate.template.parameters || []).filter(item => item.type.startsWith("endpoint") || item.type.toLocaleLowerCase() === TemplateParameterTypes.KubernetesResource.toLocaleLowerCase());
            const endpointStatus: Promise<string>[] = [];
            endpointParameters.forEach(endpointKey => {
                if (endpointKey.type.toLocaleLowerCase() === TemplateParameterTypes.KubernetesResource.toLocaleLowerCase()) {
                    endpointStatus.push(EndpointHelper.getEndpointStatus((result[TemplateConstants.K8sConnectionKey] as any).Id, this.context.pageContext));
                } else {
                    endpointStatus.push(EndpointHelper.getEndpointStatus((result[endpointKey.name] as any).Id, this.context.pageContext));
                }
            });

            try {
                await Promise.all(endpointStatus);
                this.setState({
                    progress: 0.8
                });
            } catch (error) {
                const errorObject = JSON.parse(error);
                this._recordTelemetryInfo("WaitingForEndpointsToBeReadyFailed", {
                    endpoint: errorObject.endpointId
                });
                this._handleError(errorObject.error);
            }

            try {
                this._recordTelemetryInfo("RenderingTemplateWithValues");
                this._endpointStatusCompleted = true;
                await this._renderAndSelectTemplate(
                    { ...params, ...templateParams, ...result },
                    incompleteTemplate.template,
                    incompleteTemplate.repoAnalysis,
                    incompleteTemplate.configuration);
                this._recordTelemetryInfo("RenderingTemplateWithValuesSucceeded");
            } catch (error) {
                this._recordTelemetryInfo("RenderTemplateWithValuesFailed", {
                    error: error
                });
                this._handleError(error);
            }
        }
    }

    private monitorAndUpdateProgress(): Promise<void> {
        let progressMax: number;
        if (this._fetchedCustomizedTemplates) {
            progressMax = TemplateSelector.s_progressBarMax;
        }
        else if (this._endpointStatusCompleted) {
            progressMax = 0.75;
        }
        else if (this._createdResources) {
            progressMax = 0.5;
        }
        else {
            progressMax = 0.25;
        }

        if (!this.state.errorMessage && this.state.progress < TemplateSelector.s_progressBarMax) {
            return new Promise(() => setTimeout(() => {
                let updatedProgress = this.state.progress + TemplateSelector.s_progressBarStep;
                updatedProgress = updatedProgress < progressMax ? updatedProgress : progressMax;
                if (this._mounted) {
                    this.setState({
                        progress: updatedProgress
                    });
                    this.monitorAndUpdateProgress();
                }
            }, 100));
        }

        return Promise.resolve();
    }

    private _renderAndSelectTemplate = async (
        params: { [key: string]: string | object },
        template: TemplateInfo,
        repoAnalysis?: RepositoryAnalysisInfo,
        configuration?: ConfigurationFileInfo): Promise<void> => {

        const templateService = this.context.pageContext.getService<IBuildTemplateService>("IBuildTemplateService");
        const renderedTemplate = await templateService.renderTemplate(this.props.wizardState, params);
        if (renderedTemplate.exception) {
            this.setState({
                renderingTemplate: false
            });
            this._recordTelemetryInfo("RenderTemplateWithValuesFailed", {
                error: renderedTemplate.exception
            });
            this._handleError(renderedTemplate.exception);
            return;
        }

        template.content = renderedTemplate.content;
        template.assets = renderedTemplate.assets;
        this._fetchedCustomizedTemplates = true;
        this._onTemplateSelected(template, repoAnalysis, configuration);
    }

    private _createRequiredResources = async (params: { [key: string]: object }): Promise<{ [key: string]: object }> => {
        let resourcesToCreate: { [key: string]: object } = {};
        let result: { [key: string]: object } = {};
        const templateParameters = this.state.incompleteTemplate && this.state.incompleteTemplate.template.parameters ? this.state.incompleteTemplate.template.parameters : [];
        let creatingEnvironmentResource = false;
        templateParameters.forEach(param => {
            if (param.type.toLocaleLowerCase().startsWith("endpoint:")) {
                resourcesToCreate[param.name] = {
                    "resourcetocreate": params[param.name],
                    "type": param.type
                };
            }
            else if (param.type.toLocaleLowerCase() === TemplateParameterTypes.KubernetesResource.toLocaleLowerCase()) {
                // we get a kube endpoint object for a k8s resource. Create the endpoint first and then attempt for resource creation
                resourcesToCreate[TemplateConstants.K8sConnectionKey] = {
                    "resourcetocreate": params[param.name],
                    "type": TemplateRequiredConstants.KubernetesEndpoint
                }
                creatingEnvironmentResource = true;
            }
        });

        if (!resourcesToCreate) {
            return Promise.resolve({});
        }

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.create-resources-data-provider";
        try {
            // creating endpoints first
            const endpointsCreated = await contributionService.getDataAsync<{ [key: string]: object }>(dataProviderId, resourcesToCreate) || {};
            Object.keys(endpointsCreated).forEach(key => result[key] = endpointsCreated[key]);
            if (creatingEnvironmentResource) {
                const auxillaryResourcesToCreate = await EndpointHelper.generateAuxillaryResourcesForEndpoints(templateParameters, endpointsCreated, this.props.wizardState);

                if (auxillaryResourcesToCreate) {
                    const resourcesCreated = await contributionService.getDataAsync<{ [key: string]: object }>(dataProviderId, auxillaryResourcesToCreate) || {};
                    Object.keys(resourcesCreated).forEach(key => result[key] = resourcesCreated[key]);
                }
            }
        }
        catch (error) {
            this._handleError(error);
        }

        return Promise.resolve(result);
    }

    private _parseResponse = (response: any): { [key: string]: object } => {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.create-resources-data-provider";
        const exceptions = contributionService.getDataProviderExceptions()[dataProviderId];
        if (exceptions) {
            this._handleError(exceptions);
            return {};
        }

        return response;
    }

    private _getTemplateParameters = (template: TemplateInfo): { [key: string]: string } => {
        return {
            [TemplateConstants.TemplateIdKey]: template.id,
            [TemplateConstants.RepositoryNameKey]: this.props.wizardState["repositoryName"]
        }
    }

    private _onTemplatesRetrieved = (templateData: TemplateData, action?: string) => {
        if (action === TemplateServiceEventNames.templatesRetrieved && !this._templatesFetched) {
            this._templatesFetched = true;
            this._recommendedConfigPath = templateData.configurationPath;
            if (this._mounted) {
                this.setState({
                    progress: this.state.progress + TemplateSelector.s_progressBarTemplateResults
                });
            }
        } else if (action === TemplateServiceEventNames.templatesRetrievedFail) {
            this._handleError(templateData && templateData.exception);
        }
    }

    private _getPromiseTicker(): Promise<void> {
        if ((!this._existingConfigFileFetched || !this._templatesFetched) && this.state.progress < TemplateSelector.s_progressBarMax) {
            return new Promise(() => setTimeout(() => {
                if (this._mounted) {
                    this.setState({
                        progress: this.state.progress + TemplateSelector.s_progressBarStep
                    });
                }
                this._progressTicker = this._getPromiseTicker();
            }, 100));
        }
        return Promise.resolve();
    }

    private _handleError(e: any): void {
        let errorMessage: string = "";
        if (this._mounted && e) {
            if (e.message) {
                errorMessage = e.message;
            } else if (typeof e === "string") {
                errorMessage = e;
            }

            this._normalizeHTML(errorMessage).then((normalizedMessage: string) => {
                this.setState({ errorMessage: normalizedMessage });
            });
        }
    }

    private _showFileSelectPanel = () => {
        this.setState({
            fileSelectPanelIsOpen: true,
        });
    }

    private _dismissFileSelectPanel = () => {
        this.setState({
            fileSelectPanelIsOpen: false,
        });
    }

    private _dismissRequiredInfoPanel = () => {
        this.setState({
            requiredInfoPanelIsOpen: false,
        });
    }

    private _onTemplateSelected = (template?: TemplateInfo, repoAnalysis?: RepositoryAnalysisInfo, configuration?: ConfigurationFileInfo, branch?: string) => {
        if (template && template.id === bringYourOwnYamlId) {
            this._showFileSelectPanel();
        } else {
            !this.state.errorMessage && this.props.onTemplateSelected(template, repoAnalysis, configuration, branch, this._recommendedConfigPath);
        }
    }

    private _normalizeHTML = async (content: string): Promise<string> => {
        return this._markdownPromise.then(() => {
            const historyService = this.context.pageContext.getService<IHtmlNormalizerService>("IHtmlNormalizerService");

            return historyService.normalizeStripAttributes(content);
        });
    }

    private _recordTelemetryInfo = (action: string, additionalTelemetry?: { [key: string]: any }): void => {
        let templateId = "";
        if (this.state.incompleteTemplate) {
            templateId = this.state.incompleteTemplate.template.id
        }

        const telemetryData: { [key: string]: any } = {
            action: action,
            template: templateId,
            ...additionalTelemetry
        };

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(
            "Build",
            "CreatePipeline",
            telemetryData);
    }
}

interface TemplateData {
    configurationPath: string;
    templates: TemplateInfo[];
    exception?: IServerException;
}

export interface TemplateContent {
    content: string;
    assets: FileAsset[];
    exception?: IServerException;
}

namespace TemplateServiceEventNames {
    export const templatesRetrieved = "templatesRetrieved";
    export const templatesRetrievedFail = "templatesRetrievedFail";
}

export interface IBuildTemplateService extends IVssObservableService<TemplateData> {
    getTemplates(params: WizardState): void;
    renderTemplate(wizardState: WizardState, templateParameters: { [key: string]: string | object }): Promise<TemplateContent>;
}

export class TemplateService extends VssObservableService<TemplateData> implements IBuildTemplateService {
    public getTemplates(params: WizardState): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.recommended-configuration-data-provider";
        contributionService.getDataAsync<TemplateData>(dataProviderId, params).then((templateData: TemplateData) => {
            if (templateData) {
                // do not persist the event so that future subscribers don't get inaccurate results (if a different repository was selected)
                this._notify(templateData, TemplateServiceEventNames.templatesRetrieved, false);
            }
            else {
                const ex: TemplateData = {
                    templates: [],
                    configurationPath: "",
                    exception: contributionService.getDataProviderExceptions()[dataProviderId]
                };
                // do not persist the error event, otherwise the old error will be refired
                this._notify(ex, TemplateServiceEventNames.templatesRetrievedFail, false);
            }
        });
    }

    public renderTemplate(wizardState: WizardState, templateParameters: { [key: string]: string | object }): Promise<TemplateContent> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.customized-template-data-provider";

        const requestParams: CustomizedTemplateDataProviderRequest = {
            templateParameters,
            validateAssets: true,
            sourceProviderType: wizardState.sourceProvider,
            repositoryId: wizardState.repositoryId || wizardState.repositoryName,
            connectionId: wizardState.connectionId,
            branch: wizardState.branch
        };
        return contributionService.getDataAsync<TemplateContent, CustomizedTemplateDataProviderRequest>(dataProviderId, requestParams).then((templateContent: TemplateContent) => {
            return templateContent || {
                content: "",
                exception: contributionService.getDataProviderExceptions()[dataProviderId]
            } as TemplateContent;
        });
    }
}

interface TemplatesViewProps {
    sourceProvider: string;
    progress: number;
    templateData: TemplateData | undefined;
    loadComplete: boolean;
    onTemplateSelected: (template: TemplateInfo, repoAnalysis: RepositoryAnalysisInfo) => void;
}

interface TemplatesViewState {
    showAllTemplates: boolean;
}

class TemplatesView extends VssComponent<TemplatesViewProps, TemplatesViewState, IVssComponentContext> {
    private static readonly s_empty = "empty"; // The id for the empty template
    private _ttiMarked: boolean = false;

    constructor(props: TemplatesViewProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            showAllTemplates: false
        };
    }

    public render(): JSX.Element {
        let emptyTemplate: TemplateInfo | undefined = undefined;
        let otherTemplates: TemplateInfo[] = [];
        let suggestedTemplates: TemplateInfo[] = [];

        const byoTemplate: TemplateInfo = {
            content: "",
            id: bringYourOwnYamlId,
            name: Resources.BringYourOwnYamlTitle,
            description: Resources.BringYourOwnYamlDesc,
            recommendedWeight: 0,
        };

        if (this.props.templateData) {
            for (let i = 0; i < this.props.templateData.templates.length; ++i) {
                const template = this.props.templateData.templates[i];

                if (template.id === TemplatesView.s_empty) {
                    emptyTemplate = template;
                    // Use the same icon url as the empty template
                    byoTemplate.iconUrl = emptyTemplate.iconUrl;
                    continue;
                }

                if (template.recommendedWeight > 0) {
                    suggestedTemplates.push(template);
                }
                else {
                    otherTemplates.push(template);
                }
            }

            // Sort the templates lists by name (empty template is special)
            otherTemplates = this._sortTemplatesByName(otherTemplates);
            suggestedTemplates = this._sortTemplatesByWeight(suggestedTemplates);

            // Add the empty template
            if (emptyTemplate) {
                // Add it to the end of suggested templates
                suggestedTemplates.push(emptyTemplate);
            }

            // In the case of errors during repo analysis, there may be no valid template recommendationas
            // When this happens, we don't want to suggest the byo template
            if (suggestedTemplates.length > 0) {
                // Add it to the end of suggested templates
                suggestedTemplates.push(byoTemplate);
            }
        }

        if (!(this.props.templateData && this.props.loadComplete)) {
            return <div className="analyzing-container">
                <div>{Resources.RepositoryAnalyzing}</div>
                <div>
                    <ProgressIndicator className="progress" percentComplete={this.props.progress} />
                </div>
            </div>;
        }

        const displayItems = this.state.showAllTemplates ? suggestedTemplates.concat(otherTemplates) : suggestedTemplates;

        // Get repo analysis results
        const repoAnalysis: RepositoryAnalysisInfo = {
            recommendTemplate: displayItems[0],
            suggestedTemplates: suggestedTemplates
        };
        return (displayItems.length > 0) ?
            <div className="template-view flex-column">
                <WizardList
                    wizardListItems={this._mapToWizardListItems(displayItems)}
                    onClick={(event, data) => {
                        this.props.onTemplateSelected(displayItems[data.index], repoAnalysis);
                    }}
                />
                {!this.state.showAllTemplates &&
                    <Button
                        className="all-templates-button flex-self-start"
                        onClick={this._showAllTemplates}>
                        {Resources.ShowMore}
                    </Button>
                }
            </div>
            : <div />;
    }

    public componentDidMount() {
        super.componentDidMount();

        if (!this._ttiMarked) {
            this._ttiMarked = true;
            this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService").endScenario(PerformanceConstants.Area, PageLoadScenarios.Templates, false);
        }
    }

    private _sortTemplatesByName(templates: TemplateInfo[]): TemplateInfo[] {
        if (templates && templates.length > 0) {
            return templates.sort((a, b) => {
                const leftSide = a && a.name ? a.name : "";
                const rightSide = b && b.name ? b.name : "";
                // Use locale compare on the names
                return leftSide.localeCompare(rightSide, navigator.language);
            });
        }
        return templates;
    }

    private _sortTemplatesByWeight(templates: TemplateInfo[]): TemplateInfo[] {
        if (templates && templates.length > 0) {
            return templates.sort((a, b) => {
                const leftSide = a && a.recommendedWeight ? a.recommendedWeight : 0;
                const rightSide = b && b.recommendedWeight ? b.recommendedWeight : 0;
                if (leftSide == rightSide) {
                    const leftSideName = a && a.name ? a.name : "";
                    const rightSideName = b && b.name ? b.name : "";
                    return leftSideName.localeCompare(rightSideName, navigator.language);
                }
                return rightSide - leftSide; // Descending
            });
        }
        return templates;
    }

    private _mapToWizardListItems(templates: TemplateInfo[]): IWizardListItem[] {
        return templates.map((template: TemplateInfo): IWizardListItem => {
            return {
                name: template.name,
                description: template.description,
                iconUrl: template.iconUrl,
                iconName: "FileCode"
            }
        });
    }

    private _showAllTemplates = () => {
        this.setState({ showAllTemplates: true });
    };

}

Services.add("IBuildTemplateService", { serviceFactory: TemplateService, options: ServiceOptions.Persistant });